import React from 'react';
import { theme } from '../theme';

export type PlanModel = {
  id: string;
  name: string;
  tagline?: string | null;
  price_monthly: string;
  price_yearly: string;
  agents_count: number;
  features: string[];
};

type Props = {
  plan: PlanModel;
  yearly: boolean;
  highlight?: boolean;
  agency?: boolean;
  ctaPrimary?: boolean;
  onSelect?: () => void;
  ctaLabel?: string;
};

function agentBadgeText(plan: PlanModel): string {
  if (plan.name === 'Agency') return 'Multi-client management';
  if (plan.name === 'Enterprise') return 'All agents';
  return 'AI agents';
}

export function PlanCard({
  plan,
  yearly,
  highlight,
  agency,
  ctaPrimary,
  onSelect,
  ctaLabel = 'Choose plan',
}: Props) {
  const price = yearly ? plan.price_yearly : plan.price_monthly;
  const period = yearly ? '/yr' : '/mo';
  const ctaClass = ctaPrimary ? 'odc-btn-primary' : 'odc-btn-ghost';

  return (
    <div
      className="odc-card relative flex flex-col !p-6"
      style={{
        transform: 'none',
        background: highlight ? `linear-gradient(160deg, var(--card), rgba(16,185,129,0.12))` : 'var(--card)',
        borderColor: highlight ? 'rgba(16,185,129,0.45)' : 'var(--border)',
        boxShadow: highlight ? `0 0 40px rgba(16,185,129,0.2)` : undefined,
      }}
    >
      {highlight ? (
        <span
          className="absolute -top-3 left-1/2 z-[1] -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider"
          style={{
            background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`,
            color: '#fff',
          }}
        >
          Most Popular
        </span>
      ) : null}
      {agency ? (
        <span
          className="absolute -top-3 right-3 z-[1] rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
          style={{
            background: 'rgba(6,182,212,0.2)',
            color: '#22d3ee',
            border: '1px solid rgba(6,182,212,0.45)',
          }}
        >
          For Agencies
        </span>
      ) : null}

      <h3 className="text-xl font-bold" style={{ color: 'var(--text)' }}>
        {plan.name}
      </h3>
      {plan.tagline ? (
        <p className="mt-1 text-sm leading-snug" style={{ color: 'var(--muted)' }}>
          {plan.tagline}
        </p>
      ) : null}

      <div className="mt-4 flex items-baseline gap-1">
        <span className="odc-metric !text-3xl">₹{Number(price).toLocaleString('en-IN')}</span>
        <span className="text-sm" style={{ color: 'var(--muted)' }}>
          {period}
        </span>
      </div>

      <span className="odc-badge odc-badge-accent mt-3 w-fit">{agentBadgeText(plan)}</span>

      <ul className="mt-5 flex flex-1 flex-col gap-2.5 text-sm" style={{ color: 'var(--muted)' }}>
        {plan.features.map((f) => (
          <li key={f} className="flex gap-2">
            <span style={{ color: theme.primary }}>✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className={`${ctaClass} mt-8 w-full justify-center !py-3`}
        onClick={onSelect}
      >
        {ctaLabel}
      </button>
    </div>
  );
}
